import { ApiError } from '@/api/processStepsApi'

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

/**
 * The sentence to show an operator when a request fails.
 *
 * `ApiError.message` is built for a developer reading a console — it carries the
 * method, the full URL with its ids, the status code and the raw JSON body.
 * Rendering it in the UI put things like
 *
 *   API PATCH /process-steps/executions/940cb5be…/83bde88c…/selections failed
 *   (403): {"message":"Only the assigned user can update this step's
 *   selections","error":"Forbidden","statusCode":403}
 *
 * in front of someone whose actual problem is one clause of it. The server's
 * `message` is written for them; everything around it is noise.
 *
 * Falls back to `fallback` rather than the raw text, because a message starting
 * with "API " is never something to show — if the body had nothing usable, a
 * plain sentence beats leaking the URL.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && isRecord(err.body)) {
    const { message } = err.body
    if (typeof message === 'string' && message.trim() !== '') return message
    // NestJS validation errors arrive as an array of strings.
    if (Array.isArray(message) && typeof message[0] === 'string') return message.join('. ')
  }
  if (err instanceof Error && err.message.trim() !== '' && !err.message.startsWith('API ')) {
    return err.message
  }
  return fallback
}
